import { DateTime } from 'luxon';
import { z } from 'zod';
import { TypeOfHelpSchema } from './TypeOfHelpFormSection/sectionSchema';
import { PhoneSchema } from './utils/zod-custom-schemas';

/** utilities */
const zodRequiredString = z.string().min(1, 'Required');

/** customerProfile */
const CustomerAddress = z.object({
  addressLine1: z.string().nullish().optional(),
  city: z.string().nullish().optional(),
  /** The country/region in ISO 3166 2-letter country code format.  */
  country: z.string().nullish().optional(),
  /** Not used if from customerAccountDetails, address.postalCode from duns. */
  postalCode: z.string().nullish().optional(),
  /** Not used if from customerAccountDetails, address.region from duns. */
  region: z.string().nullish().optional(),
  state: z.string().nullish().optional(),
});

const CustomerProfileIds = z.object({
  id: z.string(),
  profileType: z.enum(['External', 'duns']),
});

export const MemberInput = z.object({
  firstName: z.string().min(1, 'Required').max(100, 'Max 100 characters'),
  lastName: z.string().min(1, 'Required').max(100, 'Max 100 characters'),
  phoneNumber: PhoneSchema,
  email: z.string().min(1, 'Required').email(),
});
export type MemberInputType = z.input<typeof MemberInput>;

const CustomerProfile = z.object({
  name: z.string(),
  address: CustomerAddress,
  isMatchingComplete: z.enum(['true', 'false']).default('false'),
  isMaccEligible: z.enum(['true', 'false']).default('false'),
  ids: z.array(CustomerProfileIds).min(1, 'Required'),
  team: z.array(MemberInput).min(1, 'Required'),
});

/** details */

/**
 * details.requirements.solutions
 * solutionType:
 *  - 1P: Microsoft solution
 *  - 3P: Third-party solution
 *  - SA: -- not valid on IP Cosell referrals, so this will need to be added when needed.
 */
const BaseSolutionInput = z.object({
  type: z.literal('Name').default('Name'),
  id: z.string(),
  name: z.string(),
  publisherName: z.string(),
});

const ThirdPartySolutionInput = BaseSolutionInput.extend({
  solutionType: z.literal('3P'),
});

const MsftSolutionInput = BaseSolutionInput.extend({
  price: z.number().transform((v) => v.toString()),
  quantity: z.string().transform((v) => Number(v)),
  currency: z.string(),
  solutionType: z.literal('1P'),
});

export const SolutionInput = z.discriminatedUnion('solutionType', [
  ThirdPartySolutionInput,
  MsftSolutionInput,
]);

const Solutions = z.array(SolutionInput);

/** details.requirements */
const Requirements = z.object({
  customerMarketplaceIntent: z
    .enum(['Yes', 'No', 'HaveNotDecided'])
    .default('Yes'),
  solutionArea: zodRequiredString,
  solutionPlay: zodRequiredString,
  solutions: Solutions,
});

/** accept a date or string, but transform the output to a consistent format:
 *  eg: 2025-10-01T00:00:00.000Z
 */
export const DateOrStringToISODate = z
  .union([z.string().min(1, 'Date string cannot be empty'), z.date()])
  .transform((val) => {
    if (typeof val === 'string') {
      // For string input, parse it and set to start of day
      return DateTime.fromISO(val, { zone: 'utc' }).startOf('day').toISO();
    }
    // For Date input, convert to DateTime and set to start of day
    return DateTime.fromJSDate(val, { zone: 'utc' }).startOf('day').toISO();
  });

const Details = z.object({
  closingDateTime: DateOrStringToISODate,
  currency: zodRequiredString,
  dealValue: z.number(),
  notes: z.string().optional(),
  requirements: Requirements,
});

export const Team = z.array(MemberInput).min(1, 'Required');

export const ReferralFormSchema = z
  .object({
    customerProfile: CustomerProfile,
    details: Details,
    campaignId: z.string().optional(),
    name: zodRequiredString,
    mpnId: zodRequiredString,
    /** Salesforce crmId */
    crmId: z.string().optional(),
    typeOfHelp: TypeOfHelpSchema,
    team: Team,
  })
  .strict();

export type ReferralFormFields = z.infer<typeof ReferralFormSchema>;
